import { useMemo } from "react"
import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"

export type UsageSparklineProps = ComponentProps<"svg"> & {
  /** One value per day, oldest first (weighted daily totals). */
  series: number[]
}

// Tiny area sparkline for table cells and KPI-tile backgrounds. Hand-rolled
// SVG (no Recharts) so hundreds of table rows stay cheap; scales via
// viewBox + preserveAspectRatio, colors via currentColor. Teal (sanctioned
// data-series hue) so volume trends read apart from the amber UI chrome,
// the green day strips, and the amber/green product split.
function UsageSparkline(props: UsageSparklineProps) {
  const { series, className, ...svgProps } = props

  const path = useMemo(() => buildAreaPath(series), [series])

  if (!path) return null

  return (
    <svg
      viewBox="0 0 100 32"
      preserveAspectRatio="none"
      aria-hidden
      className={cn("usage-sparkline h-6 w-full text-teal-500", className)}
      {...svgProps}
    >
      <path
        d={`${path.line}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeOpacity="0.9"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={`${path.area}`}
        fill="currentColor"
        fillOpacity="0.15"
        stroke="none"
      />
    </svg>
  )
}

/** Line + closed area path over a 100x32 viewBox; null when all-zero. */
function buildAreaPath(series: number[]) {
  if (series.length < 2) return null
  const max = Math.max(...series)
  if (max <= 0) return null

  const step = 100 / (series.length - 1)
  // 2px top headroom, baseline at 32.
  const points = series.map((value, i) => {
    const x = i * step
    const y = 32 - (Math.max(0, value) / max) * 30
    return `${x.toFixed(2)},${y.toFixed(2)}`
  })
  const line = `M${points.join(" L")}`
  const area = `${line} L100,32 L0,32 Z`
  return { line, area }
}

export default UsageSparkline
